// Osimo — comune gratuito. Zone da geojson statici (styles.json + geojsons/),
// normativa applicata in PDF dall'area disegnata (/api/query_pdf, PostGIS).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { Button, Dock, DockSep, IconButton, LoadingOverlay, Panel } from "../components/ui";
import LegendPanel from "../components/LegendPanel";
import DocPanel from "../components/DocPanel";
import { BottomBar, FootLinks, PromoCard, ZonePicker } from "../components/MapBits";
import { useToast } from "../components/Toast";
import useDraw from "../hooks/useDraw";
import useMeasure from "../hooks/useMeasure";
import { BACKEND } from "../lib/config";
import { getJson, mapLimit, postForDownload } from "../lib/http";
import { addGoogleBasemap } from "../lib/basemap";
import { bindPattern, createHighlighter, ensurePattern, legacyZoneStyle, zonesAt } from "../lib/zones";

const CATASTO_WMS_LAYERS = {
  "CP.CadastralParcel": "CP.CadastralParcel",
  "CP.CadastralZoning": "CP.CadastralZoning",
  fabbricati: "fabbricati",
  simbolo_graffa: "simbolo_graffa",
  vestizioni: "vestizioni",
};

export default function OsimoMap({ city, onMap, docs }) {
  const toast = useToast();
  const elRef = useRef(null);
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [legend, setLegend] = useState([]);
  const layersRef = useRef({ groups: {}, items: {} });
  const zoneLayersRef = useRef([]);
  const highlighter = useMemo(createHighlighter, []);
  const [panel, setPanel] = useState(null);
  const [picker, setPicker] = useState(null);
  const [zone, setZone] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const draw = useDraw(map);
  const measure = useMeasure(map);
  const busyRef = useRef(false);
  busyRef.current = !!draw.mode || measure.active;

  // ── Mappa ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const m = L.map(elRef.current, { zoomControl: false, minZoom: 6, maxZoom: 21 })
      .setView([city.center.lat, city.center.lng], city.zoom || 14);
    L.control.zoom({ position: "bottomright" }).addTo(m);
    addGoogleBasemap(m);
    m.on("movestart", () => { highlighter.clear(); setPicker(null); });
    setMap(m);
    onMap(m);
    return () => {
      onMap(null);
      setMap(null);
      m.remove();
    };
  }, [city.key]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectZone = useCallback((layer) => {
    highlighter.apply(layer);
    setZone(layer._zoneName);
  }, [highlighter]);

  // ── Livelli da styles.json ───────────────────────────────────────────────
  useEffect(() => {
    if (!map) return undefined;
    let cancelled = false;

    const onZoneClick = (layer) => (e) => {
      if (busyRef.current) return;
      const candidates = zonesAt(zoneLayersRef.current, e.latlng);
      if (candidates.length <= 1) {
        selectZone(layer);
        setPicker(null);
        return;
      }
      const pt = map.latLngToContainerPoint(e.latlng);
      const rect = map.getContainer().getBoundingClientRect();
      setPicker({
        x: rect.left + pt.x,
        y: rect.top + pt.y,
        items: candidates.map((l) => ({ label: l._zoneName, onPick: () => selectZone(l) })),
      });
    };

    const build = async ([file, style]) => {
      const label = file.replace(/\.geojson$/i, "");
      if (style.type === "wms-proxy") {
        const wmsLayer = style.source === "catasto"
          ? (CATASTO_WMS_LAYERS[file]
              ? L.tileLayer.wms(`${BACKEND}/api/catasto`, {
                  layers: CATASTO_WMS_LAYERS[file], format: "image/png", transparent: true, version: "1.1.1",
                  opacity: style.opacity ?? 1, minZoom: 15, maxZoom: 21, attribution: "© Agenzia delle Entrate",
                })
              : null)
          : L.tileLayer.wms(style.url, {
              layers: style.layers, format: "image/png", transparent: true, opacity: style.opacity ?? 1, minZoom: 15, maxZoom: 21,
            });
        if (!wmsLayer) return null;
        return { file, label, group: style.group || "Cartografia", layer: wmsLayer, swatch: { background: "transparent", borderColor: "#9aa0ad" } };
      }

      const data = await getJson(`${BACKEND}/api/${city.key}/geojsons/${encodeURIComponent(file)}`);
      if (style.isNametag) {
        const layer = L.geoJSON(data, {
          pointToLayer: (f, latlng) =>
            L.circleMarker(latlng, { radius: 0, fillOpacity: 0, stroke: false })
              .bindTooltip(f.properties?.name || f.properties?.label || "", { permanent: true, direction: "center", className: "nametag-label" }),
        });
        return { file, label, group: style.group || "Etichette", layer, swatch: { background: "transparent", borderColor: "#f4f6fb" } };
      }

      const { style: base, pattern, swatch } = legacyZoneStyle(style);
      const patternId = pattern ? ensurePattern(map, pattern) : null;
      const layer = L.geoJSON(data, {
        style: () => base,
        onEachFeature: (_f, l) => {
          l._baseStyle = { ...base };
          l._zoneName = label;
          bindPattern(l, patternId);
          l.bindTooltip(label, { sticky: true });
          zoneLayersRef.current.push(l);
          l.on("click", onZoneClick(l));
        },
      });
      return { file, label, group: style.group || "Altro", layer, swatch };
    };

    (async () => {
      setLoading(true);
      try {
        const styles = await getJson(`${BACKEND}/api/${city.key}/styles.json`);
        const built = (await mapLimit(Object.entries(styles), 6, build, (p) => !cancelled && setProgress(p))).filter(Boolean);
        if (cancelled) return;

        const groups = {};
        const items = {};
        built.forEach((b) => {
          if (!groups[b.group]) groups[b.group] = L.layerGroup();
          groups[b.group].addLayer(b.layer);
          items[b.file] = b;
        });
        layersRef.current = { groups, items };

        const names = Object.keys(groups).sort((a, b) => a.localeCompare(b, "it"));
        if (names.length) groups[names[0]].addTo(map); // come prima: acceso solo il primo gruppo
        setLegend(names.map((name, i) => ({
          id: name,
          name,
          visible: i === 0,
          items: built.filter((b) => b.group === name).map((b) => ({ id: b.file, label: b.label, swatch: b.swatch, visible: true })),
        })));
        if (built.length < Object.keys(styles).length) toast.error("Alcuni livelli del PRG non sono stati caricati.");
      } catch {
        if (!cancelled) toast.error("Impossibile caricare i livelli del PRG.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      Object.values(layersRef.current.groups).forEach((g) => map.removeLayer(g));
      layersRef.current = { groups: {}, items: {} };
      zoneLayersRef.current = [];
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Legenda ──────────────────────────────────────────────────────────────
  const toggleGroup = (id) => {
    const g = layersRef.current.groups[id];
    const grp = legend.find((x) => x.id === id);
    if (!g || !grp || !map) return;
    if (grp.visible) map.removeLayer(g); else g.addTo(map);
    setLegend((list) => list.map((x) => (x.id === id ? { ...x, visible: !grp.visible } : x)));
  };

  // only: elenco di voci da cambiare (es. quelle filtrate); senza, tutte le voci del gruppo.
  const setItems = (groupId, on, only) => {
    const g = layersRef.current.groups[groupId];
    const grp = legend.find((x) => x.id === groupId);
    if (!g || !grp) return;
    const ids = only ? new Set(only.map((i) => i.id)) : null;
    grp.items.forEach((it) => {
      if (ids && !ids.has(it.id)) return;
      const layer = layersRef.current.items[it.id]?.layer;
      if (layer) { if (on) g.addLayer(layer); else g.removeLayer(layer); }
    });
    setLegend((list) => list.map((x) => (x.id !== groupId ? x : {
      ...x,
      items: x.items.map((it) => (!ids || ids.has(it.id) ? { ...it, visible: on } : it)),
    })));
  };

  const toggleItem = (groupId, itemId) => {
    const grp = legend.find((g) => g.id === groupId);
    const it = grp?.items.find((i) => i.id === itemId);
    if (it) setItems(groupId, !it.visible, [it]);
  };

  // ── Strumenti ────────────────────────────────────────────────────────────
  const startDraw = (kind) => {
    measure.stop();
    draw.start(kind);
  };

  const toggleMeasure = () => {
    if (measure.active) { measure.stop(); return; }
    draw.cancel();
    measure.start();
  };

  const extractPdf = async () => {
    if (!draw.shape) {
      toast.info("Disegna prima l'area: poligono o rettangolo.");
      return;
    }
    setPdfBusy(true);
    try {
      await postForDownload(`${BACKEND}/api/query_pdf`, draw.shape, "NORME APPLICATE ALLA TUA ISTANZA.pdf", {
        fallbackError: "Nessuna zona del PRG trovata nell'area disegnata.",
      });
      toast.ok("PDF della normativa scaricato.");
    } catch (e) {
      toast.error(e.message || "Estrazione del PDF non riuscita.");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <>
      <div ref={elRef} className="sm-map" />

      <Dock>
        <IconButton icon="layers" label="Legenda" active={panel === "legend"}
          onClick={() => setPanel((p) => (p === "legend" ? null : "legend"))} />
        <DockSep />
        <IconButton icon="polygon" label="Disegna un poligono" active={draw.mode === "polygon"} onClick={() => startDraw("polygon")} />
        <IconButton icon="square" label="Disegna un rettangolo" active={draw.mode === "rectangle"} onClick={() => startDraw("rectangle")} />
        <IconButton icon="trash" label="Cancella il disegno" disabled={!draw.shape} onClick={draw.clear} />
        <DockSep />
        {docs?.available && (
          <IconButton icon="file-text" label="Documento intervento" active={panel === "docs"}
            onClick={() => setPanel((p) => (p === "docs" ? null : "docs"))} />
        )}
        <IconButton icon="ruler" label="Misura distanze" active={measure.active} onClick={toggleMeasure} />
      </Dock>

      {panel === "docs" && (
        <DocPanel city={city} shape={draw.shape} auth={docs.auth} onDraw={startDraw} onClose={() => setPanel(null)} />
      )}

      {panel === "legend" && (
        <LegendPanel
          groups={legend}
          onToggleGroup={toggleGroup}
          onToggleItem={toggleItem}
          onSetItems={setItems}
          filterable
          onClose={() => setPanel(null)}
        />
      )}

      {zone && (
        <Panel side="right" title="Zona PRG" onClose={() => { setZone(null); highlighter.clear(); }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: "var(--fs-md)" }}>{zone}</p>
          <p className="sm-panel-text">
            Per la normativa applicata disegna l'area che ti interessa ed estrai il PDF: comprende tutte le zone che l'area tocca.
          </p>
        </Panel>
      )}

      <ZonePicker picker={picker} onClose={() => setPicker(null)} />

      <BottomBar measure={measure} draw={draw} idleHint="Disegna un'area per estrarre la normativa applicata in PDF">
        <span className="sm-label">Area pronta</span>
        <Button variant="document" icon="file-text" busy={pdfBusy} onClick={extractPdf}>Estrai normativa PDF</Button>
        {docs?.available && (
          <Button variant="secondary" size="sm" icon="file-text" onClick={() => setPanel("docs")}>Documento intervento</Button>
        )}
        <Button variant="ghost" size="sm" icon="trash" onClick={draw.clear}>Cancella</Button>
      </BottomBar>

      <PromoCard />
      <FootLinks />
      {loading && <LoadingOverlay label="Caricamento zone del PRG" progress={progress} />}
    </>
  );
}
